import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { FinancialStatusBadge, useOrders } from '@/features/orders';

const LIMIT = 10;

/** The latest orders of one shopper, read from the orders list filtered by customer. */
export function CustomerOrdersCard({
  storeId,
  customerId,
}: {
  storeId: string;
  customerId: string;
}) {
  const { t } = useTranslation('customers');
  const format = useFormat();
  const query = useOrders(storeId, { customerId, limit: LIMIT });
  const orders = query.data?.items ?? [];

  return (
    <Card className="gap-3" data-tour="customers.detail.orders">
      <CardHeader>
        <CardTitle>{t('orders.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-24" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : orders.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('orders.empty')}</p>
        ) : (
          <>
            <Table aria-label={t('orders.title')}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('orders.order')}</TableHead>
                  <TableHead>{t('orders.placed')}</TableHead>
                  <TableHead className="text-right">{t('orders.total')}</TableHead>
                  <TableHead>{t('orders.payment')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <Link
                        to="/s/$storeId/orders/$orderId"
                        params={{ storeId, orderId: order.id }}
                        className="font-mono text-[13px] font-medium hover:underline"
                      >
                        {order.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground" title={format.utc(order.placedAt)}>
                      {format.relative(order.placedAt)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {format.money(order.total)}
                    </TableCell>
                    <TableCell>
                      <FinancialStatusBadge status={order.financialStatus} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {query.data.nextCursor ? (
              <p className="text-muted-foreground mt-2 text-xs">
                {t('orders.more', { count: LIMIT })}
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
